import { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogIn, LogOut, Coffee, Undo2, CloudOff, CheckCircle2, ScanFace, Search, X } from "lucide-react";
import { rotuloFuncionario } from "@/features/ponto/pontoApi";
import { CapturaFacial } from "@/features/ponto/CapturaFacial";
import type { ResultadoExtracao } from "@/features/ponto/faceEmbedding";

/** Payload da batida (o backend resolve CPF/nome pelo IdFuncionario). */
interface BatidaPayload {
  IdFuncionario: number;
  DtHoraRegistro: string;
  ITipoBatida: number;
  IOrigem: number;
  SIdClientGuid: string;
  FLatitude: number | null;
  FLongitude: number | null;
}
interface Funcionario { id: number; nome: string; sMatricula: string; sCnpjCpf: string; }

const FILA_KEY = "ponto_fila_offline";
/** Último cadastro de funcionários baixado — o quiosque busca nele mesmo sem conexão. */
const CACHE_KEY = "ponto_funcionarios_cache";
const lerCache = (): Funcionario[] => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) ?? "[]"); } catch { return []; } };
const semAcento = (s: string) => (s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const lerFila = (): BatidaPayload[] => { try { return JSON.parse(localStorage.getItem(FILA_KEY) ?? "[]"); } catch { return []; } };
const gravarFila = (f: BatidaPayload[]) => localStorage.setItem(FILA_KEY, JSON.stringify(f));

/** Data/hora local do dispositivo, sem fuso ("YYYY-MM-DDTHH:mm:ss") — wall-clock REP-P. */
function agoraLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function geoAtual(): Promise<{ lat: number | null; lon: number | null }> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lon: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve({ lat: null, lon: null }),
      { timeout: 4000, maximumAge: 60000 }
    );
  });
}

const TIPOS = [
  { id: 1, label: "Entrada", icon: LogIn },
  { id: 2, label: "Saída (intervalo)", icon: Coffee },
  { id: 3, label: "Retorno (intervalo)", icon: Undo2 },
  { id: 4, label: "Saída", icon: LogOut },
];

export default function BatidaPage() {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>(lerCache);
  const [funcionarioId, setFuncionarioId] = useState(0);
  const [busca, setBusca] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pendentes, setPendentes] = useState(lerFila().length);
  const [ultima, setUltima] = useState<{ nsr?: number; hash?: string } | null>(null);
  const [captura, setCaptura] = useState(false);
  const [reconhecido, setReconhecido] = useState<string>("");
  const sincronizando = useRef(false);

  const sincronizar = async () => {
    if (sincronizando.current) return;
    sincronizando.current = true;
    try {
      let fila = lerFila();
      while (fila.length > 0) {
        await apiClient.post("/api/rh/ponto/batida", fila[0]);
        fila = fila.slice(1);
        gravarFila(fila);
        setPendentes(fila.length);
      }
    } catch { /* segue offline; tenta no próximo 'online' */ }
    finally { sincronizando.current = false; }
  };

  useEffect(() => {
    apiClient.get<Funcionario[]>("/api/rh/ponto/funcionarios", { params: { somenteAtivos: true } })
      .then((r) => {
        const enxuto = r.data.map(({ id, nome, sMatricula, sCnpjCpf }) => ({ id, nome, sMatricula, sCnpjCpf }));
        setFuncionarios(enxuto);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(enxuto)); } catch { /* cota cheia: segue sem cache */ }
      })
      .catch(() => { /* offline: quiosque usa o último cache do navegador */ });
    if (navigator.onLine) void sincronizar();
    const on = () => void sincronizar();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identificarPorRosto = async (r: ResultadoExtracao) => {
    const resp = await apiClient.post("/api/rh/ponto/faces/identificar", { Embedding: r.embedding });
    const res = resp.data as { aceito: boolean; idFuncionario: number; mensagem?: string };
    if (!res.aceito || res.idFuncionario <= 0) {
      throw new Error(res.mensagem || "Funcionário não reconhecido. Selecione manualmente.");
    }
    setFuncionarioId(res.idFuncionario);
    setBusca("");
    const f = funcionarios.find((x) => x.id === res.idFuncionario);
    const nome = f ? rotuloFuncionario(f) : "funcionário reconhecido (cadastro desatualizado neste aparelho)";
    setReconhecido(nome);
    toast.success(`Reconhecido: ${nome}`);
  };

  const bater = async (tipo: number) => {
    if (!funcionarioId) { toast.warning("Selecione o funcionário."); return; }
    setEnviando(true);
    const geo = await geoAtual();
    const payload: BatidaPayload = {
      IdFuncionario: funcionarioId,
      DtHoraRegistro: agoraLocal(),
      ITipoBatida: tipo,
      IOrigem: 1, // kiosk
      SIdClientGuid: crypto.randomUUID(),
      FLatitude: geo.lat,
      FLongitude: geo.lon,
    };

    try {
      const r = await apiClient.post("/api/rh/ponto/batida", payload);
      const res = r.data?.resultado;
      setUltima({ nsr: res?.lNsr ?? res?.LNsr, hash: res?.sHashAfd ?? res?.SHashAfd });
      toast.success("Batida registrada.");
    } catch (e: any) {
      if (!e.response) {
        const fila = [...lerFila(), payload];
        gravarFila(fila);
        setPendentes(fila.length);
        setUltima(null);
        toast.info("Sem conexão — batida guardada e será sincronizada.");
      } else {
        toast.error(e.message ?? "Falha ao registrar a batida.");
      }
    } finally { setEnviando(false); }
  };

  // Busca do quiosque: matrícula exata primeiro, depois matrícula/CPF/nome contendo o texto.
  const encontrados = useMemo(() => {
    const t = semAcento(busca.trim());
    if (!t) return [];
    const dig = busca.replace(/\D/g, "");
    const numerica = dig.length >= 3 && dig.length * 2 >= t.replace(/\s/g, "").length;
    return funcionarios
      .filter((f) => semAcento(f.nome).includes(t) || semAcento(f.sMatricula).includes(t)
        || (numerica && (f.sCnpjCpf ?? "").replace(/\D/g, "").includes(dig)))
      .sort((a, b) => Number(b.sMatricula === busca.trim()) - Number(a.sMatricula === busca.trim()) || a.nome.localeCompare(b.nome))
      .slice(0, 8);
  }, [busca, funcionarios]);
  const selecionado = funcionarios.find((f) => f.id === funcionarioId);
  const escolher = (f: Funcionario) => { setFuncionarioId(f.id); setBusca(""); setReconhecido(""); };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Registrar Ponto</h1>
        {pendentes > 0 && (
          <Badge variant="secondary" className="gap-1">
            <CloudOff className="h-3.5 w-3.5" /> {pendentes} na fila offline
          </Badge>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>Identificação</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1 flex-1 min-w-[240px]">
              <Label htmlFor="busca-kiosk">Matrícula, CPF ou nome</Label>
              {funcionarioId > 0 ? (
                <div className="flex items-center gap-2 h-14 px-4 border rounded-md bg-muted/30">
                  <span className="text-lg font-medium truncate flex-1">
                    {selecionado ? rotuloFuncionario(selecionado) : reconhecido || "Funcionário selecionado"}
                  </span>
                  <Button variant="ghost" size="icon" aria-label="Trocar funcionário" onClick={() => { setFuncionarioId(0); setReconhecido(""); }}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-4 h-6 w-6 text-muted-foreground" />
                  <Input id="busca-kiosk" className="h-14 pl-12 text-lg" autoComplete="off" value={busca}
                    placeholder="Digite e toque no seu nome"
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && encontrados.length > 0) escolher(encontrados[0]); }} />
                </div>
              )}
            </div>
            <Button variant="secondary" onClick={() => setCaptura(true)} className="gap-2 h-14">
              <ScanFace className="h-5 w-5" />Reconhecer por rosto
            </Button>
          </div>
          {funcionarioId === 0 && busca.trim() && (
            <div className="mt-2 grid gap-2">
              {encontrados.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ninguém encontrado com "{busca}".</p>
              ) : encontrados.map((f) => (
                <Button key={f.id} variant="outline" className="h-12 justify-start text-base" onClick={() => escolher(f)}>
                  {rotuloFuncionario(f)}
                </Button>
              ))}
            </div>
          )}
          {reconhecido && (
            <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />Reconhecido por biometria: <strong>{reconhecido}</strong>
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        {TIPOS.map((t) => (
          <Button key={t.id} size="lg" variant="outline" disabled={enviando} onClick={() => bater(t.id)} className="h-24 text-lg flex-col gap-2">
            <t.icon className="h-7 w-7" />
            {t.label}
          </Button>
        ))}
      </div>

      {ultima && (
        <Card className="border-green-500/40">
          <CardContent className="flex items-center gap-3 pt-6">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
            <div>
              <div className="font-semibold">Batida registrada</div>
              <div className="text-sm text-muted-foreground">
                NSR {ultima.nsr != null ? String(ultima.nsr).padStart(9, "0") : "—"}
                {ultima.hash && <> · verificação {ultima.hash.slice(0, 12)}…</>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <CapturaFacial
        open={captura}
        onOpenChange={setCaptura}
        titulo="Reconhecer por rosto"
        descricao="Olhe para a câmera. A imagem não sai do dispositivo — só o vetor é comparado."
        rotuloCaptura="Reconhecer"
        onCapturar={identificarPorRosto}
      />
    </div>
  );
}
